import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Coffee, Heart, Sparkles, Lock, Check } from "lucide-react";
import { toast } from "sonner";

const TIPS = [
  { amount: 3, label: "A coffee", note: "Fuels one feature" },
  { amount: 5, label: "A latte", note: "Most popular", featured: true },
  { amount: 10, label: "A studio day", note: "Keeps the lights on" },
  { amount: 25, label: "A patron", note: "Goes on our wall" },
];

export default function Support() {
  const { user } = useAuth();
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tip = async (amount) => {
    if (!amount || amount < 1) { toast.error("Pick an amount first"); return; }
    setSubmitting(true);
    try {
      await api.post("/donations", { amount: Number(amount), message: "" });
      toast.success(`Thank you for the $${amount} tip ☕`);
      setCustom("");
    } catch {
      toast.error("Could not record tip");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
      {/* Hero */}
      <div className="grid grid-cols-12 gap-10 items-end border-b border-[#E5E5E5] pb-16">
        <div className="col-span-12 md:col-span-7">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#9CA47E] mb-4 inline-flex items-center gap-2">
            <Heart className="w-3.5 h-3.5 fill-[#9CA47E]" /> Free for everyone, forever
          </div>
          <h1 className="font-heading font-medium text-5xl sm:text-7xl tracking-tight leading-[0.95]">
            Support<br /> the <span className="italic">collective</span>.
          </h1>
          <p className="font-body text-lg text-[#595959] mt-6 max-w-xl leading-relaxed">
            VoraStyle is free to use — every feature, every category, no ads. If the canvas helps you create, drop a tip in the jar so we can keep the servers humming and the community open.
          </p>
        </div>
        <div className="col-span-12 md:col-span-5">
          <div className="border border-[#333333] bg-[#333333] text-white p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E] mb-3">⌗ Where tips go</div>
            <ul className="space-y-3 font-body text-sm text-white/85">
              <li className="flex gap-3"><Check className="w-4 h-4 mt-0.5 text-[#9CA47E]" /> Hosting + image storage</li>
              <li className="flex gap-3"><Check className="w-4 h-4 mt-0.5 text-[#9CA47E]" /> Background-removal model & APIs</li>
              <li className="flex gap-3"><Check className="w-4 h-4 mt-0.5 text-[#9CA47E]" /> Building new editor tools</li>
              <li className="flex gap-3"><Check className="w-4 h-4 mt-0.5 text-[#9CA47E]" /> Featuring community curators</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tip jar */}
      <div className="mt-16">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#9CA47E] mb-4">◇ Tip jar</div>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">Buy us a coffee.</h2>
        <p className="font-body text-[#595959] mt-3 max-w-xl">One-time tip — pick an amount or set your own. No account needed (but logged-in tips show on your profile soon).</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#E5E5E5] mt-10">
          {TIPS.map((t, i) => (
            <button
              key={t.amount}
              data-testid={`tip-${t.amount}`}
              onClick={() => tip(t.amount)}
              disabled={submitting}
              className={`text-left p-8 ${i > 0 ? "border-l border-[#E5E5E5]" : ""} transition-colors ${t.featured ? "bg-[#9CA47E] text-white hover:bg-[#7E876A]" : "bg-white hover:bg-[#F2EDE3]"}`}
            >
              <Coffee className={`w-5 h-5 ${t.featured ? "text-white" : "text-[#333333]"}`} />
              <div className="font-heading text-4xl mt-4">${t.amount}</div>
              <div className={`font-body text-sm mt-1 ${t.featured ? "text-white/85" : "text-[#333333]"}`}>{t.label}</div>
              <div className={`font-mono text-[10px] uppercase tracking-widest mt-2 ${t.featured ? "text-white/70" : "text-[#595959]"}`}>{t.note}</div>
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md">
          <Input
            data-testid="tip-custom-input"
            type="number"
            min="1"
            step="1"
            placeholder="Custom amount $"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className="rounded-none border-[#E5E5E5] focus-visible:ring-0 focus-visible:border-[#333333] py-6"
          />
          <Button data-testid="tip-custom-submit" onClick={() => tip(custom)} disabled={submitting} className="rounded-none bg-[#333333] hover:bg-[#333] text-white py-6 px-8">
            <Coffee className="w-4 h-4 mr-2" /> Send tip
          </Button>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#595959] mt-4">
          Prefer external? <a href="https://www.buymeacoffee.com" target="_blank" rel="noreferrer" className="underline underline-offset-4 text-[#9CA47E]">Buy Me a Coffee →</a>
        </div>
      </div>

      {/* Future membership */}
      <div className="mt-24 border border-[#E5E5E5] bg-[#F2EDE3] p-10 sm:p-14 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
        <div className="md:col-span-2">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#333333] mb-3 inline-flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Coming later · 2026
          </div>
          <h3 className="font-heading text-3xl sm:text-4xl tracking-tight">VoraStyle Atelier <span className="italic text-[#595959]">(membership)</span></h3>
          <p className="font-body text-[#595959] mt-3 leading-relaxed max-w-2xl">
            We're sketching a future <em>Atelier</em> tier with custom domains, set analytics, brand collabs and earlier access to new editor tools.
            Right now <strong>every existing feature is and will remain free</strong> — Atelier is purely additive.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6 font-body text-sm">
            {["Custom domains for sets", "Audience analytics", "Priority web clipper", "Brand partnership tools"].map((f) => (
              <li key={f} className="flex gap-2"><Sparkles className="w-3.5 h-3.5 mt-1 text-[#9CA47E]" />{f}</li>
            ))}
          </ul>
        </div>
        <div>
          <Button disabled data-testid="atelier-waitlist" className="rounded-none w-full bg-[#333333] text-white py-6 cursor-not-allowed opacity-70">
            <Lock className="w-4 h-4 mr-2" /> Atelier · Coming Later
          </Button>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#595959] mt-3 text-center">
            Placeholder · no signup yet
          </div>
        </div>
      </div>

      {/* Free pledge */}
      <div className="mt-24 text-center max-w-2xl mx-auto">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#9CA47E] mb-3">§ Our pledge</div>
        <h3 className="font-heading text-3xl sm:text-4xl tracking-tight">Always free at the core.</h3>
        <p className="font-body text-[#595959] mt-4 leading-relaxed">
          Whatever happens, the canvas, the clipper and the community will stay free.
          {user ? <> Thanks for being here, <strong>@{user.username}</strong>. ✦</> : null}
        </p>
      </div>
    </div>
  );
}
